import { esc } from '../calc/format.js';

/* --------------------------------------------------------------------------
   Hjälp: ett litet frågetecken som fäller ut en kort förklaring

   helpButton('alternativt') ger knappens HTML och kan läggas var som helst,
   även i tabeller som ritas om. Texterna står här, på ett ställe; en ny
   förklaring är en ny post i HELP.

   Klick på knappen öppnar en ruta under den och flyttar fokus dit, så att
   den som använder tangentbord eller skärmläsare hamnar i förklaringen.
   Escape, eller Tab förbi länken, stänger rutan och lämnar fokus på knappen
   igen. Klick utanför eller ett nytt klick på knappen stänger den också.
   Bara en ruta är öppen åt gången, och closeHelp() anropas när sidan ritas
   om, så att rutan aldrig blir kvar när knappen försvinner.

   Länkar öppnas i en ny flik: kunden ska inte tappa sin analys, som bara
   finns i den här fliken.
   -------------------------------------------------------------------------- */

export const HELP = {
  alternativt: {
    label: 'Vad är ett alternativt betalsätt?',
    title: 'Alternativt betalsätt',
    text: 'Alternativa betalsätt är betalsätt som butiken själv lägger upp och namnger i EasyCashier. ' +
          'Här visas de med sitt nummer: Alternativt betalsätt 1, 2 och så vidare.',
    link: {href: 'https://easycashier.se/support/alternativa-betalsatt', text: 'Läs mer om alternativa betalsätt'}
  },
  bonuscheck: {
    label: 'Vad är en bonuscheck?',
    title: 'Bonuscheckar',
    text: 'Kunder i lojalitetsprogrammet samlar poäng när de handlar. När poängen når gränsen butiken ställt in ' +
          'blir de en bonuscheck på ett belopp kunden själv väljer. Här visas checkarna som lösts in i kassan. ' +
          'Är köpet mindre än checken förbrukas ändå hela checken. Checkar som skapats men inte lösts in finns ' +
          'inte i exporten.'
  },
  rabatt: {
    label: 'Vad räknas som rabatt på artikel?',
    title: 'Rabatt på artikel',
    text: 'Ett extrapris är en rabatt i procent eller kronor på en artikel, med start- och sluttid. ' +
          'I exporten ser ett extrapris ut precis som en rabatt som lämnats i kassan, så de redovisas tillsammans. ' +
          'Kampanjer, som 3 för 2, redovisas för sig.'
  },
  tryck: {
    label: 'Hur räknas trycket i kassan?',
    title: 'Mest att göra i kassan',
    text: 'Varje ruta är antalet köp den timmen en genomsnittlig dag: alla köp den veckodagen och timmen, ' +
          'delat med antalet sådana dagar med försäljning i perioden. En månad har fyra eller fem fredagar, ' +
          'så en summa skulle visa fel. Antalet köp avgör bemanningen bättre än omsättningen, eftersom ett stort köp ' +
          'inte tar mycket längre tid i kassan än ett litet. På en dator visar en ruta det högsta antalet köp ' +
          'en enskild dag när du håller muspekaren över den: det är toppen som avgör bemanningen.'
  },
  korg: {
    label: 'Hur räknas det som köps tillsammans?',
    title: 'Köps ofta tillsammans',
    text: 'Ett köp är ett kvitto. Andelen är hur många av köpen med den valda artikeln som också hade den andra. ' +
          '”Gånger så ofta som vanligt” jämför med hur ofta den andra artikeln köps i alla köp. Bara artiklar som ' +
          'köps minst 1,2 gånger så ofta tillsammans, i minst 3 köp, visas. En bärkasse finns i många köp men ' +
          'säger inget om just den här artikeln. Returer räknas inte. Med en artikelgrupp vald räknas ändå hela kvittot.'
  }
};

/** HTML för en hjälpknapp. key måste finnas i HELP. */
export function helpButton(key){
  const h = HELP[key];
  if(!h) throw new Error('Ingen hjälptext för ' + key);
  return `<button type="button" class="help-btn" data-help="${esc(key)}" aria-label="${esc(h.label)}"
    aria-expanded="false" aria-haspopup="dialog">?</button>`;
}

let open = null;   // {btn, box}

/** Stänger en öppen ruta. Anropas också när tabellerna ritas om. */
export function closeHelp(returnFocus = false){ close(returnFocus); }

function close(returnFocus){
  if(!open) return;
  const {btn, box} = open;
  open = null;
  box.remove();
  btn.setAttribute('aria-expanded', 'false');
  btn.removeAttribute('aria-controls');
  if(returnFocus && btn.isConnected) btn.focus();
}

function show(btn){
  const h = HELP[btn.dataset.help];
  if(!h) return;
  const box = document.createElement('div');
  box.className = 'help-pop';
  box.id = 'helpPop';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-labelledby', 'helpPopTitle');
  box.innerHTML = `<strong id="helpPopTitle">${esc(h.title)}</strong><p>${esc(h.text)}</p>` +
    (h.link ? `<a href="${esc(h.link.href)}" target="_blank" rel="noopener noreferrer">${esc(h.link.text)}</a>` : '');
  // Rutan ligger i dokumentet, inte i tabellen: en tabell som ritas om får
  // inte ta den med sig, och overflow i tabellens behållare får inte klippa den
  document.body.appendChild(box);
  const r = btn.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 32);
  box.style.width = width + 'px';
  box.style.left = Math.max(16, Math.min(r.left + window.scrollX, window.scrollX + window.innerWidth - width - 16)) + 'px';
  box.style.top = (r.bottom + window.scrollY + 6) + 'px';
  btn.setAttribute('aria-expanded', 'true');
  btn.setAttribute('aria-controls', box.id);
  box.tabIndex = -1;
  box.addEventListener('keydown', e => {
    if(e.key !== 'Tab') return;
    const link = box.querySelector('a');
    const last = link || box;
    // Ut ur rutan åt något håll: stäng och fortsätt från knappen
    if((!e.shiftKey && document.activeElement === last) || (e.shiftKey && document.activeElement === box)){
      e.preventDefault();
      close(true);
    }
  });
  open = {btn, box};
  box.focus();
}

/** Kopplar händelserna en gång för hela sidan. */
export function initHelp(){
  document.addEventListener('click', e => {
    const btn = e.target.closest?.('.help-btn');
    if(btn){
      const same = open && open.btn === btn;
      close(false);
      if(!same) show(btn);
      return;
    }
    if(open && !open.box.contains(e.target)) close(false);
  });
  document.addEventListener('keydown', e => {
    if(e.key === 'Escape' && open) close(true);
  });
  // Rutan är placerad efter knappens läge; ändras fönstret stämmer det inte längre
  window.addEventListener('resize', () => close(false));
}
